<template>
    <FormSection title="Referral Source" :full-height="false" :application-id="referral?.application">
        <template v-slot:content>
            <section id="Referral_Source" class="row p-4 pb-0">
                <div class="col-12">
                    <h6 class="mb-2 d-flex align-items-center">
                        <b class="me-2">Referral Information</b>
                    </h6>
                    <p>
                        Use the filters below to narrow down physicians by city and/or state. To search for a physician,
                        enter the physician's name or NPI in the respective search field.
                    </p>
                </div>
                <section class="form-group col-12 d-flex flex-column">
                    <b class="me-2">Filters</b>
                    <section class="col-12 flex-row py-2 px-3 row g-0 mb-2">
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> City </label>
                            <TypeaheadInput
                                :getData="getReferringPhysicianListItem"
                                :value="referringPhysicianFilters.city"
                                v-on:valueChanged="referringPhysicianFiltersCity"
                                :results-limit="1"
                                :init-value="referringPhysicianFilters.city"
                                itemName="city"
                                placeholder="Select City"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> State </label>
                            <TypeaheadInput
                                :getData="getReferringPhysicianListItem"
                                :value="referringPhysicianFilters.state"
                                v-on:valueChanged="referringPhysicianFiltersState"
                                :init-value="referringPhysicianFilters.state"
                                :results-limit="2"
                                itemName="state"
                                placeholder="Select State"
                            />
                        </div>
                    </section>
                </section>
                <section class="form-group col-12 d-flex flex-column">
                    <b class="mb-2">Search</b>
                    <section class="col-12 flex-row px-3 row g-0 mb-2 align-items-center">
                        <div class="form-group card p-2 d-flex col-12 col-lg-6 pe-lg-3 flex-row">
                            <div class="form-group col-lg-6 ms-3">
                                <label for="referral-source">
                                    Referring Physician
                                    <span class="text-danger">*</span>
                                    <a
                                        tabindex="0"
                                        href="javascript:void(0);"
                                        role="button"
                                        v-custom-popover="{ content: sanitizeMarkup(refPhysicianTooltip) }"
                                    >
                                        <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                    </a>
                                </label>
                                <TypeaheadInput
                                    :getData="getReferringPhysicianList"
                                    :value="referringPhysicianName"
                                    v-on:valueChanged="referringPhysicianNameChange"
                                    placeholder="Search by Name"
                                    @blur="v$.referringPhysicianName.$touch"
                                    :class="`${!v$.referringPhysicianName.$error ? '' : 'field-invalid'}`"
                                >
                                </TypeaheadInput>
                                <span v-if="v$.referringPhysicianName.$error" class="field-invalid"
                                    >Referring Physician is required.</span
                                >
                            </div>
                            <div
                                class="form-group col-lg-5 form-check custom-checkbox form-check-inline align-items-center justify-content-end"
                            >
                                <input
                                    type="checkbox"
                                    id="signing-plan-of-care"
                                    class="form-check-input au-target"
                                    @change="signingPOCChanged()"
                                    v-model="referral.ReferralExtension.IsSigningPlanOfCare"
                                />
                                <label class="form-check-label flex-grow-0" for="signing-plan-of-care">
                                    <span class="me-1"> Signing Plan of Care</span>
                                    <a
                                        tabindex="0"
                                        href="javascript:void(0);"
                                        role="button"
                                        v-custom-popover="{ content: sanitizeMarkup(signingPOCTooltip) }"
                                    >
                                        <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                    </a>
                                </label>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-1 text-center">
                            <strong>OR</strong>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> NPI </label>
                            <TypeaheadInput
                                :getData="getReferringPhysicianListItem"
                                :value="referringPhysicianFilters.npi"
                                v-on:valueChanged="referringPhysicianFiltersNpiChange"
                                :results-limit="3"
                                itemName="npi"
                                :key-updated="npiChanged('referring')"
                                placeholder="Search by NPI"
                            />
                        </div>
                    </section>
                </section>
                <section class="form-group col-12 d-flex flex-column">
                    <b class="me-2">Face-to-Face Evaluation Information</b>
                    <div class="form-group p-2 col-12 col-lg-6 pe-lg-3 flex-row">
                        <div class="col-12 col-lg-10 d-flex g-0">
                            <input
                                v-model="faceToFaceEvaluation"
                                :value="null"
                                name="face-to-face-evaluation"
                                type="radio"
                                au-target-id="791"
                                id="evaluationna"
                            />
                            <label class="px-2" for="evaluationna">N/A</label>
                        </div>
                        <div class="col-12 d-flex col-lg-10 mb-0 g-0">
                            <input
                                v-model="faceToFaceEvaluation"
                                :value="true"
                                name="face-to-face-evaluation"
                                type="radio"
                                au-target-id="791"
                                id="evaluationdate"
                            />
                            <label class="px-2" for="evaluationdate">Date of face-to-face visit</label>
                        </div>
                        <div class="col-12 col-lg-10 d-flex g-0">
                            <div v-if="faceToFaceEvaluation" class="form-group offset-lg-1">
                                <DatePicker :getDate="onDateChange">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            type="text"
                                            name="evaluation-date"
                                            class="form-control"
                                            placeholder="MM/DD/YYYY"
                                            :value="formatDateInput(referral.ReferralExtension.DateOfFaceToFaceVisit)"
                                            @change="handleDateChange"
                                            autocomplete="off"
                                            @blur="v$.referral.ReferralExtension.DateOfFaceToFaceVisit.$touch"
                                            :class="`${
                                                !v$.referral.ReferralExtension.DateOfFaceToFaceVisit.$error
                                                    ? ''
                                                    : 'field-invalid'
                                            }`"
                                        />
                                        <div class="input-group-addon d-flex input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                            <label><span class="text-danger">*</span></label>
                                        </div>
                                        <span
                                            v-if="v$.referral.ReferralExtension.DateOfFaceToFaceVisit.$error"
                                            class="field-invalid"
                                            >Date of Face-to-Face Visit is required.</span
                                        >
                                    </div>
                                </DatePicker>
                            </div>
                        </div>
                        <div class="col-12 col-lg-10 d-flex g-0">
                            <input
                                v-model="faceToFaceEvaluation"
                                :value="false"
                                name="face-to-face-evaluation"
                                type="radio"
                                au-target-id="791"
                                id="evaluationwithindays"
                            />
                            <label class="px-2" for="evaluationwithindays">
                                Face-to-face to be completed within 30 days
                            </label>
                        </div>
                    </div>
                </section>
                <section class="form-group col-12 d-flex row">
                    <template v-if="showCertifyPhysicianFilters">
                        <div class="col-12">
                            <h6 class="mb-2 d-flex align-items-center">
                                <b class="me-2">Certifying Physician Information</b>
                            </h6>
                            <p>
                                Use the filters below to narrow down physicians by city and/or state. To search for a
                                physician, enter the physician’s name or NPI in the respective search field.
                            </p>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <div class="form-check custom-checkbox form-check-inline ps-0">
                                <input
                                    type="checkbox"
                                    hidden
                                    id="certifying-physician-filters"
                                    class="form-check-input au-target hidden"
                                    v-model="showCertifyPhysicianFilters"
                                />
                                <label for="certifying-physician-filters" class="card px-2 py-2 cursor-pointer">
                                    <span class="me-1 text-secondary">Hide Search Parameters</span>
                                </label>
                            </div>
                        </div>
                        <section class="col-12 d-flex flex-column">
                            <b class="me-2">Filters</b>
                            <section class="col-12 flex-row py-2 px-3 row g-0 mb-2">
                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                    <label for="referral-source"> City </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianListItem"
                                        :value="certifyingPhysicianFilters.city"
                                        v-on:valueChanged="certifyingPhysicianFiltersCityChange"
                                        :init-value="certifyingPhysicianFilters.city"
                                        itemName="city"
                                        placeholder="Select City"
                                    />
                                </div>
                                <div class="form-group col-12 col-lg-4 pe-lg-3">
                                    <label for="referral-source"> State </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianListItem"
                                        :value="certifyingPhysicianFilters.state"
                                        v-on:valueChanged="certifyingPhysicianFiltersStateChange"
                                        :init-value="certifyingPhysicianFilters.state"
                                        itemName="state"
                                        placeholder="Select State"
                                    />
                                </div>
                            </section>
                        </section>
                    </template>
                    <section class="form-group col-12 d-flex flex-column">
                        <b class="me-2" v-if="showCertifyPhysicianFilters">Search</b>
                        <section
                            class="flex-row row g-0 mb-2 align-items-center"
                            :class="showCertifyPhysicianFilters && 'col-12'"
                        >
                            <div
                                class="form-group card p-2 d-flex col-12 pe-lg-3 col-lg-8 flex-row form-check-inline align-items-center justify-content-between"
                            >
                                <div class="form-group ps-3 col-lg-8">
                                    <label for="referral-source">
                                        Certifying Physician
                                        <a
                                            tabindex="0"
                                            href="javascript:void(0);"
                                            role="button"
                                            v-custom-popover="{ content: sanitizeMarkup(certifyingPhysicianTooltip) }"
                                        >
                                            <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                        </a>
                                    </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianList"
                                        :value="certifyingPhysicianName"
                                        v-on:valueChanged="certifyingPhysicianNameChange"
                                        placeholder="Enter Certifying Physician"
                                    />
                                </div>
                                <div class="form-check custom-checkbox form-check-inline">
                                    <input
                                        type="checkbox"
                                        id="referral-same-attending-physician"
                                        class="form-check-input mt-2"
                                        v-model="referral.ReferralExtension.IsPrimaryCarePhysician"
                                        @click="IsPrimaryCarePhysicianChange()"
                                    />
                                    <label class="form-check-label mt-1" for="referral-same-attending-physician">
                                        <span class="me-1 mb-2"> Attending Physician </span>
                                        <a
                                            tabindex="0"
                                            href="javascript:void(0);"
                                            role="button"
                                            v-custom-popover="{ content: sanitizeMarkup(attendingPhysicianTooltip) }"
                                        >
                                            <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                        </a>
                                    </label>
                                </div>
                            </div>
                            <template v-if="showCertifyPhysicianFilters">
                                <div class="form-group col-12 col-lg-1 text-center">
                                    <strong>OR</strong>
                                </div>
                                <div class="form-group col-12 col-lg-3 pe-lg-3">
                                    <label for="referral-source"> NPI </label>
                                    <TypeaheadInput
                                        :getData="getCertifyingPhysicianListItem"
                                        :value="certifyingPhysicianFilters.npi"
                                        :valueChanged="certifyingPhysicianFiltersNpiChange"
                                        itemName="npi"
                                        placeholder="Search By NPI"
                                        :results-limit="3"
                                        :key-updated="npiChanged('certifying')"
                                    />
                                </div>
                            </template>
                            <div class="form-group col-12 col-lg-3 pe-lg-3" v-if="!showCertifyPhysicianFilters">
                                <div class="form-check custom-checkbox form-check-inline justify-content-end">
                                    <input
                                        type="checkbox"
                                        hidden
                                        id="certifying-physician-filters"
                                        class="form-check-input au-target hidden"
                                    />
                                </div>
                                <label for="certifying-physician-filters" class="card px-2 py-2 cursor-pointer">
                                    <span class="me-1 text-center text-secondary"> View More Search Parameters</span>
                                </label>
                            </div>
                        </section>
                    </section>
                    <section class="col-12 ps-2 row g-0">
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="referral-source"> Name of Referral Source</label>
                            <input
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Name of Referral Source"
                                v-model="referral.Referral.OtherReferralSource"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="community-liaison"> Community Liaison </label>
                            <select class="form-select" v-model="referral.Referral.CommunityLiaison">
                                <option v-if="!isEditing" value="">--Select Option--</option>
                                <option v-else :value="'00000000-0000-0000-0000-000000000000'">
                                    --Select Option--
                                </option>
                                <option
                                    v-for="(cmtLiaison, index) of referralOptions.communityLiaison"
                                    :key="index"
                                    :value="cmtLiaison.value"
                                >
                                    {{ cmtLiaison.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="referral-source">
                                <span>Facility Referral Source</span>
                            </label>

                            <select class="form-select" v-model="referral.Referral.FacilitySource">
                                <option v-if="!isEditing" value="">--Select Option--</option>
                                <option v-else :value="'00000000-0000-0000-0000-000000000000'">
                                    --Select Option--
                                </option>
                                <option
                                    v-for="(rflOption, index) of referralOptions.facilitySource"
                                    :key="index"
                                    :value="rflOption.value"
                                >
                                    {{ rflOption.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="internal-referral-source-name"> Internal Referral Source </label>

                            <select class="form-select" v-model="referral.Referral.InternalReferral">
                                <option v-if="!isEditing" value="">--Select Option--</option>
                                <option v-else :value="'00000000-0000-0000-0000-000000000000'">
                                    --Select Option--
                                </option>
                                <option
                                    v-for="(intrlRef, index) of referralOptions.internalReferral"
                                    :key="index"
                                    :value="intrlRef.value"
                                >
                                    {{ intrlRef.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="admission-source-name">
                                Admission Source <span class="text-danger">*</span>
                            </label>
                            <select
                                class="form-select"
                                v-model="referral.Referral.AdmissionSource"
                                @blur="v$.referral.Referral.AdmissionSource.$touch"
                                :class="`${!v$.referral.Referral.AdmissionSource.$error ? '' : 'field-invalid'}`"
                            >
                                <option :value="0">--Select Option--</option>
                                <option
                                    v-for="(admSource, index) of referralOptions?.admissionSource"
                                    :key="index"
                                    :value="admSource.value"
                                >
                                    {{ admSource.text }}
                                </option>
                            </select>
                            <span v-if="v$.referral.Referral.AdmissionSource.$error" class="field-invalid"
                                >Admission Source is required.</span
                            >
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="assign-to-clinical-manager"> Assign To Clinical Manager</label>
                            <select class="form-select" v-model="referral.Referral.ClinicalManagerAssigned">
                                <option value="">--Select Option--</option>
                                <option :value="'00000000-0000-0000-0000-000000000000'">--Select Option--</option>
                                <option
                                    v-for="(clinMngr, index) of referralOptions.clinicalManager"
                                    :key="index"
                                    :value="clinMngr.value"
                                >
                                    {{ clinMngr.text }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="type-of-inpatient-code"
                                >Type of Inpatient Admission/Visit Code
                                <span class="text-danger">*</span>
                                <a
                                    tabindex="0"
                                    href="javascript:void(0);"
                                    role="button"
                                    v-custom-popover="{ content: sanitizeMarkup(inpatientCodeTooltip) }"
                                >
                                    <i class="fas fa-info-circle text-primary font-size-xs"></i>
                                </a>
                            </label>
                            <select
                                class="form-select"
                                v-model="referral.Referral.AdditionalBillingInfo.AdmissionType"
                                @blur="v$.referral.Referral.AdditionalBillingInfo.AdmissionType.$touch"
                                :class="`${
                                    !v$.referral.Referral.AdditionalBillingInfo.AdmissionType.$error
                                        ? ''
                                        : 'field-invalid'
                                }`"
                            >
                                <option :value="0">--Select Option--</option>
                                <option
                                    v-for="(admType, index) of referralOptions.admissionType"
                                    :key="index"
                                    :value="admType.value"
                                >
                                    {{ admType.text }}
                                </option>
                            </select>
                            <span
                                v-if="v$.referral.Referral.AdditionalBillingInfo.AdmissionType.$error"
                                class="field-invalid"
                                >Admission Type is required.</span
                            >
                        </div>
                        <div class="form-group col-12 col-lg-6 pe-lg-2">
                            <label for="referral-date">
                                Referral Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :getDate="onDateChange">
                                <div class="input-group date datepickerInput">
                                    <input
                                        type="text"
                                        name="referral-date"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(referral.Referral.ReferralDate)"
                                        @change="handleDateChange"
                                        autocomplete="off"
                                        @blur="v$.referral.Referral.ReferralDate.$touch"
                                        :class="`${!v$.referral.Referral.ReferralDate.$error ? '' : 'field-invalid'}`"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                    <span v-if="v$.referral.Referral.ReferralDate.$error" class="field-invalid"
                                        >Referral Date is required.</span
                                    >
                                </div>
                            </DatePicker>
                        </div>
                    </section>
                </section>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ReferralSourceInformation.ts"></script>
